/**
 * Dossiers de coffre. Le NOM d'un dossier peut etre sensible (ex. « Comptes
 * a resilier », « Ex »...) : il est donc chiffre comme le contenu d'une
 * entree, sous la `vaultKey`, via `@klavox/crypto-core`. Seuls `id`,
 * `parentId`, `revision` et les horodatages restent en clair — l'arbre de
 * classement est visible du serveur, jamais les noms. Ces metadonnees sont
 * AUTHENTIFIEES (liees a l'AEAD, `aad.ts`) : le serveur ne peut pas les modifier.
 */
import { decryptJson, encryptJson, type EncryptedEntry, type SecretKey } from '@klavox/crypto-core';
import {
  assertCount,
  assertId,
  assertNullableCount,
  assertNullableId,
  openWithLegacyFallback,
  recordAad,
} from './aad.js';
import { resolveNow } from './clock.js';
import { InvalidVaultModelInputError } from './errors.js';
import { generateId } from './ids.js';

export interface VaultFolderSecrets {
  readonly name: string;
  /**
   * Dossier « sur en voyage » (R18, mode voyage) : son contenu RESTE sur
   * l'appareil quand le mode voyage est actif, tout le reste en est retire.
   * Le drapeau vit dans les secrets CHIFFRES, jamais en metadonnee : le
   * serveur ne doit pas apprendre quels dossiers un voyageur emporte -- ce
   * serait dire a qui saisit l'appareil ce qui manque. Absent = non sur.
   */
  readonly travelSafe?: boolean;
}

export interface VaultFolderMetadata {
  readonly id: string;
  readonly parentId: string | null;
  readonly revision: number;
  readonly createdAt: number;
  readonly updatedAt: number;
  readonly deletedAt: number | null;
}

export interface VaultFolder extends VaultFolderMetadata {
  readonly secrets: VaultFolderSecrets;
}

export interface EncryptedVaultFolder extends VaultFolderMetadata {
  readonly secrets: EncryptedEntry;
}

export interface CreateVaultFolderInput {
  readonly name: string;
  readonly parentId?: string | null;
  readonly travelSafe?: boolean;
  readonly now?: number;
}

function assertVaultFolderMetadata(folder: unknown): asserts folder is VaultFolderMetadata {
  if (folder === null || typeof folder !== 'object') {
    throw new InvalidVaultModelInputError('folder: objet attendu');
  }
  const candidate = folder as Partial<VaultFolderMetadata>;
  if (typeof candidate.id !== 'string' || candidate.id.length === 0) {
    throw new InvalidVaultModelInputError('folder.id: chaine non vide attendue');
  }
}

function assertFolderName(name: unknown): asserts name is string {
  if (typeof name !== 'string' || name.length === 0) {
    throw new InvalidVaultModelInputError('name: chaine non vide attendue');
  }
}

/** Cree un nouveau dossier (revision 1, non supprime). */
export function createVaultFolder(input: CreateVaultFolderInput): VaultFolder {
  if (input === null || typeof input !== 'object') {
    throw new InvalidVaultModelInputError('input: objet attendu');
  }
  assertFolderName(input.name);
  const now = resolveNow(input.now);
  return {
    id: generateId(),
    parentId: input.parentId ?? null,
    revision: 1,
    createdAt: now,
    updatedAt: now,
    deletedAt: null,
    secrets: input.travelSafe === true ? { name: input.name, travelSafe: true } : { name: input.name },
  };
}

function strictVaultFolderMetadata(folder: unknown): VaultFolderMetadata {
  if (folder === null || typeof folder !== 'object') {
    throw new InvalidVaultModelInputError('folder: objet attendu');
  }
  const c = folder as Record<keyof VaultFolderMetadata, unknown>;
  assertId(c.id, 'folder.id');
  assertNullableId(c.parentId, 'folder.parentId');
  assertCount(c.revision, 'folder.revision');
  assertCount(c.createdAt, 'folder.createdAt');
  assertCount(c.updatedAt, 'folder.updatedAt');
  assertNullableCount(c.deletedAt, 'folder.deletedAt');
  return {
    id: c.id,
    parentId: c.parentId,
    revision: c.revision,
    createdAt: c.createdAt,
    updatedAt: c.updatedAt,
    deletedAt: c.deletedAt,
  };
}

function vaultFolderAad(m: VaultFolderMetadata): string {
  return recordAad('folder', [m.id, m.parentId, m.revision, m.createdAt, m.updatedAt, m.deletedAt]);
}

export interface EncryptVaultFolderInput {
  readonly folder: VaultFolder;
  readonly vaultKey: SecretKey;
}

/** Chiffre le nom du dossier sous la `vaultKey`, metadonnees liees en AAD (format v2). */
export function encryptVaultFolder(input: EncryptVaultFolderInput): EncryptedVaultFolder {
  const metadata = strictVaultFolderMetadata(input?.folder);
  const ciphertext = encryptJson({ entry: input.folder.secrets, vaultKey: input.vaultKey, entryId: vaultFolderAad(metadata) });
  return { ...metadata, secrets: ciphertext };
}

export interface DecryptVaultFolderInput {
  readonly folder: EncryptedVaultFolder;
  readonly vaultKey: SecretKey;
  /** Voir `DecryptVaultItemInput.acceptLegacyFormat` : migration locale uniquement. */
  readonly acceptLegacyFormat?: boolean;
}

/**
 * Dechiffre un dossier.
 *
 * @throws {import('@klavox/crypto-core').DecryptionFailedError} mauvaise
 *   `vaultKey`, blob altere, blob substitue depuis un autre dossier, ou
 *   metadonnee modifiee depuis le chiffrement
 */
export function decryptVaultFolder(input: DecryptVaultFolderInput): VaultFolder {
  const metadata = strictVaultFolderMetadata(input?.folder);
  const open = (entryId: string) => () =>
    decryptJson<VaultFolderSecrets>({ ciphertext: input.folder.secrets, vaultKey: input.vaultKey, entryId });
  const plaintext = openWithLegacyFallback(input.acceptLegacyFormat, open(vaultFolderAad(metadata)), open(metadata.id));
  if (plaintext === null || typeof plaintext !== 'object' || typeof plaintext.name !== 'string') {
    throw new InvalidVaultModelInputError('dossier dechiffre: forme inattendue');
  }
  if (plaintext.travelSafe !== undefined && typeof plaintext.travelSafe !== 'boolean') {
    throw new InvalidVaultModelInputError('dossier dechiffre: travelSafe booleen attendu');
  }
  return { ...metadata, secrets: plaintext };
}

/** Renomme un dossier et fait progresser sa revision. */
export function renameVaultFolder(folder: VaultFolder, name: string, now?: number): VaultFolder {
  assertVaultFolderMetadata(folder);
  assertFolderName(name);
  if (folder.secrets.name === name) return folder;
  // `...folder.secrets` et non `{ name }` : renommer un dossier ne doit pas lui
  // retirer son drapeau « sur en voyage » (R18).
  return { ...folder, secrets: { ...folder.secrets, name }, revision: folder.revision + 1, updatedAt: resolveNow(now) };
}

/**
 * Marque (ou demarque) un dossier « sur en voyage » (R18). Comme un renommage :
 * le drapeau est dans les secrets, la revision progresse pour que le
 * changement se propage aux autres appareils a la prochaine synchro.
 */
export function setVaultFolderTravelSafe(folder: VaultFolder, travelSafe: boolean, now?: number): VaultFolder {
  assertVaultFolderMetadata(folder);
  if (typeof travelSafe !== 'boolean') {
    throw new InvalidVaultModelInputError('travelSafe: booleen attendu');
  }
  if ((folder.secrets.travelSafe ?? false) === travelSafe) return folder;
  const { travelSafe: _previous, ...rest } = folder.secrets;
  return {
    ...folder,
    secrets: travelSafe ? { ...rest, travelSafe: true } : rest,
    revision: folder.revision + 1,
    updatedAt: resolveNow(now),
  };
}

/**
 * Deplace un dossier sous un autre parent (`null` = racine).
 *
 * @throws {InvalidVaultModelInputError} un dossier ne peut pas etre son
 *   propre parent (boucle immediate) ; la detection de cycles plus profonds
 *   est a la charge de la couche applicative, qui connait l'arbre complet.
 */
export function moveVaultFolder<T extends VaultFolderMetadata>(
  folder: T,
  parentId: string | null,
  now?: number,
): T {
  assertVaultFolderMetadata(folder);
  if (parentId === folder.id) {
    throw new InvalidVaultModelInputError('folder: un dossier ne peut pas etre son propre parent');
  }
  if (folder.parentId === parentId) return folder;
  return { ...folder, parentId, revision: folder.revision + 1, updatedAt: resolveNow(now) } as T;
}

/** Suppression logique (tombstone), symetrique de `markVaultItemDeleted`. */
export function markVaultFolderDeleted<T extends VaultFolderMetadata>(folder: T, now?: number): T {
  assertVaultFolderMetadata(folder);
  const at = resolveNow(now);
  if (folder.deletedAt !== null) return folder;
  return { ...folder, deletedAt: at, revision: folder.revision + 1, updatedAt: at } as T;
}

/** Annule une suppression logique tant que le dossier n'a pas ete purge. */
export function restoreVaultFolder<T extends VaultFolderMetadata>(folder: T, now?: number): T {
  assertVaultFolderMetadata(folder);
  if (folder.deletedAt === null) return folder;
  return { ...folder, deletedAt: null, revision: folder.revision + 1, updatedAt: resolveNow(now) } as T;
}
